import {
  type IBotPartTf2ConfigItem,
  type SkillSizeTier,
  skillSizeTiers,
  StatTypeTf2,
  type TerraformingPlayerStats
} from '@nova/types';

const TERRAFORMING_PART_COUNT = 4;

export const terraformingPartStatValues = {
  [StatTypeTf2.seedCapacity]: [10, 20, 30],
  [StatTypeTf2.skillSize]: skillSizeTiers,
  [StatTypeTf2.movementSpeed]: [0.8, 1, 1.2]
} as const;

function getPartStat(part: IBotPartTf2ConfigItem, stat: StatTypeTf2, partIndex: number): number {
  const value = part[stat];
  if (value === undefined || !Number.isFinite(value)) {
    throw new Error(`[resolveTerraformingPlayerStats] part ${partIndex + 1} requires a finite ${stat} value`);
  }

  const allowedValues: readonly number[] = terraformingPartStatValues[stat];
  if (!allowedValues.some((allowedValue) => allowedValue === value)) {
    throw new Error(`[resolveTerraformingPlayerStats] part ${partIndex + 1} has unsupported ${stat} value <${value}>`);
  }

  return value;
}

function mean(values: readonly number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function resolveSkillSizeTier(skillSize: number): SkillSizeTier {
  let nearestTier: SkillSizeTier = skillSizeTiers[0];
  for (const tier of skillSizeTiers) {
    if (Math.abs(tier - skillSize) <= Math.abs(nearestTier - skillSize)) {
      nearestTier = tier;
    }
  }
  return nearestTier;
}

export function resolveTerraformingPlayerStats(parts: readonly IBotPartTf2ConfigItem[]): TerraformingPlayerStats {
  if (parts.length !== TERRAFORMING_PART_COUNT) {
    throw new Error(`[resolveTerraformingPlayerStats] expected ${TERRAFORMING_PART_COUNT} parts, received ${parts.length}`);
  }

  const seedCapacities = parts.map((part, index) => getPartStat(part, StatTypeTf2.seedCapacity, index));
  const movementSpeeds = parts.map((part, index) => getPartStat(part, StatTypeTf2.movementSpeed, index));
  const skillSizes = parts.map((part, index) => getPartStat(part, StatTypeTf2.skillSize, index));

  return {
    seedCapacity: seedCapacities.reduce((sum, capacity) => sum + capacity, 0),
    movementSpeed: Math.round(mean(movementSpeeds) * 100) / 100,
    skillSize: resolveSkillSizeTier(mean(skillSizes))
  };
}
